// --------------------------------------------------------------
// Created On: 2021-11-03
// Author: Zachary Thomas
//
// Last Modified: 2026-04-13
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import { useNavigate } from "react-router-dom";
import { RPM_COMPANY } from "../../../constants/miscellaneous";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import useReduxSelector from "../../../hooks/useReduxSelector";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import styles from "./CompanyOptions.module.scss";

// Company dropdown for returning to RPM admin dashboard.
export default function CompanyOptions(): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  // Exit the current company and return to the RPM admin dashboard.
  function exitCompany(): void {
    dispatch(
      setCurrentUser({
        userId: currentUser.userId,
        userName: currentUser.userName,
        emailAddress: currentUser.emailAddress,
        originalCompanyId: currentUser.originalCompanyId,
        originalCompanyName: RPM_COMPANY,
        companyId: currentUser.originalCompanyId,
        companyName: RPM_COMPANY,
        subsidiaryCompanyId: 0,
        subsidiaryCompanyName: "",
        isCompanyHidden: false,
        permissions: currentUser.permissions,
        isPackager: false,
        mfaEnabled: currentUser.mfaEnabled,
        isInSubsidiary: false,
      })
    );
    navigate("/");
  }

  // Exit the current subsidiary and return to the parent company.
  function exitSubsidiary(): void {
    dispatch(
      setCurrentUser({
        userId: currentUser.userId,
        userName: currentUser.userName,
        emailAddress: currentUser.emailAddress,
        originalCompanyId: currentUser.originalCompanyId,
        originalCompanyName: currentUser.originalCompanyName,
        companyId: currentUser.companyId,
        companyName: currentUser.companyName,
        subsidiaryCompanyId: 0,
        subsidiaryCompanyName: "",
        isCompanyHidden: false,
        permissions: currentUser.permissions,
        isPackager: false,
        mfaEnabled: currentUser.mfaEnabled,
        isInSubsidiary: false,
      })
    );
    navigate("/manage/subsidiaries", { replace: true });
  }

  return (
    <div className={`${styles.body} dropdown me-3`}>
      {/* Button that displays the current company. */}
      <button
        className={`${styles.dropdownContainer} btn btn-primary dropdown-toggle py-0`}
        type="button"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <div className="d-flex align-items-center">
          <div className={`${styles.companyNameContainer} mx-3`}>
            <div className={`${styles.companyName} d-none d-xxl-inline`}>
              {currentUser.isInSubsidiary ? currentUser.subsidiaryCompanyName : currentUser.companyName}
            </div>
            <i className={`${styles.companyIcon} fa fa-fw fa-rocket d-inline d-xxl-none`} />
          </div>
        </div>
      </button>

      {/* Dropdown menu of options. */}
      <div className="dropdown-menu dropdown-menu-end">
        <button
          className={`${styles.option} dropdown-item`}
          onClick={() => {
            currentUser.isInSubsidiary ? exitSubsidiary() : exitCompany();
          }}
        >
          Exit &apos;
          {currentUser.isInSubsidiary ? currentUser.subsidiaryCompanyName : currentUser.companyName}&apos;
        </button>
      </div>
    </div>
  );
}
